import React, { useState, useEffect, useCallback } from 'react';
import {
  MemberReview,
  CreateMemberReviewPayload,
  UpdateMemberReviewPayload,
} from '../../types/memberReview';
import {
  listReviewsForMember,
  createReview,
  updateReview,
  deleteReview,
} from '../../services/memberReviewService';
import { authService } from '../../services/auth';
import './ReservationReviewSection.css';

interface Props {
  reservationId: number;
  memberId: number;
  onReviewSaved?: () => void;
}

const STAR_LABELS = ['Terrible', 'Bad', 'Okay', 'Good', 'Excellent'];

const VISIBILITY_OPTIONS = [
  { value: 'store', label: 'This campground only' },
  { value: 'corporate', label: 'Corporate chain' },
  { value: 'public', label: 'All campgrounds' },
] as const;

const StarPicker: React.FC<{ value: number; onChange: (v: number) => void }> = ({
  value,
  onChange,
}) => {
  const [hover, setHover] = useState(0);
  const active = hover || value;
  return (
    <div className="rrv-star-picker">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          className={`rrv-star-btn${active >= n ? ' filled' : ''}`}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          title={STAR_LABELS[n - 1]}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
        >
          ★
        </button>
      ))}
      {active > 0 && <span className="rrv-star-label">{STAR_LABELS[active - 1]}</span>}
    </div>
  );
};

export const StarDisplay: React.FC<{ rating: number; size?: 'sm' | 'md' }> = ({
  rating,
  size = 'md',
}) => (
  <span className={`rrv-star-display rrv-star-display--${size}`} aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map(n => (
      <span key={n} className={n <= rating ? 'rrv-star-filled' : 'rrv-star-empty'}>
        ★
      </span>
    ))}
  </span>
);

const ReservationReviewSection: React.FC<Props> = ({
  reservationId,
  memberId,
  onReviewSaved,
}) => {
  const [review, setReview] = useState<MemberReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState({
    rating: 0,
    comment: '',
    wouldWelcomeBack: true,
    visibility: 'store' as 'store' | 'corporate' | 'public',
  });

  const storeId = authService.getStoreId();

  const fetchReview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const reviews = await listReviewsForMember(memberId);
      const match = reviews.find(r => r.reservationId === reservationId) ?? null;
      setReview(match);
    } catch (e: any) {
      setError(e.message || 'Failed to load review');
    } finally {
      setLoading(false);
    }
  }, [memberId, reservationId]);

  useEffect(() => {
    fetchReview();
  }, [fetchReview]);

  const openAddForm = () => {
    setForm({ rating: 0, comment: '', wouldWelcomeBack: true, visibility: 'store' });
    setIsEditing(true);
    setError(null);
  };

  const openEditForm = () => {
    if (!review) return;
    setForm({
      rating: review.rating,
      comment: review.comment || '',
      wouldWelcomeBack: review.wouldWelcomeBack,
      visibility: review.visibility,
    });
    setIsEditing(true);
    setError(null);
  };

  const handleSave = async () => {
    if (form.rating === 0) {
      setError('Please select a star rating.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (review) {
        const payload: UpdateMemberReviewPayload = {
          rating: form.rating,
          comment: form.comment || undefined,
          would_welcome_back: form.wouldWelcomeBack,
          visibility: form.visibility,
        };
        await updateReview(review.id, payload);
      } else {
        const payload: CreateMemberReviewPayload = {
          member_id: memberId,
          reservation_id: reservationId,
          rating: form.rating,
          comment: form.comment || undefined,
          would_welcome_back: form.wouldWelcomeBack,
          visibility: form.visibility,
        };
        await createReview(payload);
      }
      await fetchReview();
      setIsEditing(false);
      onReviewSaved?.();
    } catch (e: any) {
      setError(e.message || 'Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!review) return;
    setSaving(true);
    setError(null);
    try {
      await deleteReview(review.id);
      setReview(null);
      setConfirmDelete(false);
      onReviewSaved?.();
    } catch (e: any) {
      setError(e.message || 'Failed to delete review');
    } finally {
      setSaving(false);
    }
  };

  const isOwnReview = review !== null && storeId !== null && review.storeId === storeId;

  return (
    <section className="rrv-section">
      <h3 className="rrv-title">Member Review</h3>

      {loading ? (
        <p className="rrv-loading">Loading review...</p>
      ) : isEditing ? (
        <div className="rrv-form">
          <StarPicker value={form.rating} onChange={r => setForm(f => ({ ...f, rating: r }))} />

          <textarea
            className="rrv-comment-input"
            placeholder="Add a comment (optional)..."
            value={form.comment}
            onChange={e => setForm(f => ({ ...f, comment: e.target.value }))}
            rows={3}
          />

          <label className="rrv-welcome-label">
            <input
              type="checkbox"
              checked={form.wouldWelcomeBack}
              onChange={e => setForm(f => ({ ...f, wouldWelcomeBack: e.target.checked }))}
            />
            Would welcome back
          </label>

          <div>
            <label className="rrv-field-label">Visibility</label>
            <select
              className="rrv-visibility-select"
              value={form.visibility}
              onChange={e =>
                setForm(f => ({ ...f, visibility: e.target.value as 'store' | 'corporate' | 'public' }))
              }
            >
              {VISIBILITY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {error && <p className="rrv-error">{error}</p>}

          <div className="rrv-form-actions">
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Review'}
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setIsEditing(false);
                setError(null);
              }}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : review ? (
        <div className="rrv-display">
          <div className="rrv-display-header">
            <StarDisplay rating={review.rating} />
            {!review.wouldWelcomeBack && (
              <span className="rrv-flagged-badge">Not Welcome Back</span>
            )}
            {review.storeName && (
              <span className="rrv-store-name">— {review.storeName}</span>
            )}
          </div>

          {review.comment && <p className="rrv-comment">"{review.comment}"</p>}

          <p className="rrv-meta">
            Reviewed {new Date(review.createdAt).toLocaleDateString()}
            {review.updatedAt ? ' (edited)' : ''}
          </p>

          {isOwnReview && (
            <div className="rrv-actions">
              {confirmDelete ? (
                <>
                  <span className="rrv-confirm-text">Delete this review?</span>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={handleDelete}
                    disabled={saving}
                  >
                    {saving ? 'Deleting...' : 'Confirm Delete'}
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setConfirmDelete(false)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button className="btn btn-secondary btn-sm" onClick={openEditForm}>
                    Edit
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => setConfirmDelete(true)}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          )}

          {error && <p className="rrv-error">{error}</p>}
        </div>
      ) : (
        <button className="rrv-add-btn" onClick={openAddForm}>
          + Rate this stay
        </button>
      )}
    </section>
  );
};

export default ReservationReviewSection;
